import { ICoreContext } from '@msdyn365-commerce/core-internal';
import axios from 'axios';
import { IFormData, SupportFormOptionLabels } from '../cus-support-form.data';
import { IItemsLabelsData } from '../cus-support-form.props.autogenerated';
import { useState } from 'react';

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            }
        };

        reader.onerror = error => reject(error);

        reader.readAsDataURL(file);
    });
}

export default function useSupportFormRequests(
    formData: IFormData,
    ctx: ICoreContext,
    supportEmail: string,
    supportSubject: string,
    translatedLabels: IItemsLabelsData,
    clearFormData: () => void,
    context: ICoreContext
) {
    const [isToasterOpen, setIsToasterOpen] = useState(false);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const submitSupportForm = async () => {
        setIsFormLoading(true);
        const items = await Promise.all(
            formData.items.map(async item => {
                const image = await fileToBase64(item.image!);
                return {
                    ItemNumber: item.itemNumber,
                    Size: item.size,
                    Image: item.image
                        ? {
                              FileName: item.image.name,
                              ContentType: item.image.type,
                              Base64Data: image.split('base64,')[1]
                          }
                        : null
                };
            })
        );

        try {
            const apiData = {
                url: `${ctx.request.apiSettings.baseUrl}Commerce/SubmitSupportForm`,
                payload: {
                    formData: {
                        SupportEmail: supportEmail,
                        Subject: supportSubject || 'Subject:',
                        PhoneNumber: formData.phoneNumber,
                        Name: formData.name,
                        Email: formData.email,
                        Topic: SupportFormOptionLabels(translatedLabels)[formData.topic],
                        Description: formData.description,
                        OrderNumber: formData.orderNumber,
                        Items: items
                    }
                },
                headers: { oun: ctx.request.apiSettings.oun }
            };

            ctx.telemetry.error('SubmitSupportForm request', {
                url: apiData.url,
                payload: apiData.payload,
                headers: apiData.headers
            });

            const response = await axios.post(apiData.url, apiData.payload, {
                headers: apiData.headers
            });

            clearFormData();
            setIsToasterOpen(true);
            setIsFormLoading(false);
            setIsFormSubmitted(true);
            if (!response.data.SuccessStatusCode) {
                ctx.telemetry.error('SubmitSupportForm failed', {
                    message: response.data?.Message || 'Unknown error'
                });
            }

            return response.data;
        } catch (error) {
            ctx.telemetry.error('SubmitSupportForm failed', {
                message: error instanceof Error ? error.message : 'Unknown error',
                error
            });

            setIsFormLoading(false);
            throw new Error('Failed to submit support form');
        }
    };

    return {
        submitSupportForm,
        isToasterOpen,
        setIsToasterOpen,
        isFormLoading,
        isFormSubmitted
    };
}
